import { useState, useMemo } from 'react';

const TRACKER_GAIN = 1.45; // 45% more energy than fixed
const FIXED_EFFICIENCY = 0.80; // fixed panel efficiency factor

const Calculator = () => {
  const [systemSize, setSystemSize] = useState(100);       // kW
  const [sunHours, setSunHours] = useState(5.5);           // hrs/day
  const [electricityRate, setElectricityRate] = useState(0.12); // $/kWh
  const [installCostFixed, setInstallCostFixed] = useState(800);  // $/kW
  const [installCostTracker, setInstallCostTracker] = useState(1100); // $/kW

  const results = useMemo(() => {
    const fixedAnnualKwh = systemSize * sunHours * FIXED_EFFICIENCY * 365;
    const trackerAnnualKwh = fixedAnnualKwh * TRACKER_GAIN;
    const extraKwh = trackerAnnualKwh - fixedAnnualKwh;

    const fixedAnnualSavings = fixedAnnualKwh * electricityRate;
    const trackerAnnualSavings = trackerAnnualKwh * electricityRate;
    const extraSavings = extraKwh * electricityRate;

    const fixedCapex = systemSize * installCostFixed;
    const trackerCapex = systemSize * installCostTracker;

    const fixedPayback = fixedAnnualSavings > 0 ? fixedCapex / fixedAnnualSavings : 0;
    const trackerPayback = trackerAnnualSavings > 0 ? trackerCapex / trackerAnnualSavings : 0;

    const fixed25yrROI = ((fixedAnnualSavings * 25 - fixedCapex) / fixedCapex) * 100;
    const tracker25yrROI = ((trackerAnnualSavings * 25 - trackerCapex) / trackerCapex) * 100;

    const co2PerKwh = 0.42; // kg CO₂ per kWh (Egypt grid average)
    const trackerCo2 = (trackerAnnualKwh * co2PerKwh) / 1000; // tonnes/yr

    return {
      fixedAnnualKwh: Math.round(fixedAnnualKwh),
      trackerAnnualKwh: Math.round(trackerAnnualKwh),
      extraKwh: Math.round(extraKwh),
      fixedAnnualSavings: Math.round(fixedAnnualSavings),
      trackerAnnualSavings: Math.round(trackerAnnualSavings),
      extraSavings: Math.round(extraSavings),
      fixedCapex: Math.round(fixedCapex),
      trackerCapex: Math.round(trackerCapex),
      fixedPayback: fixedPayback.toFixed(1),
      trackerPayback: trackerPayback.toFixed(1),
      fixed25yrROI: Math.round(fixed25yrROI),
      tracker25yrROI: Math.round(tracker25yrROI),
      trackerCo2: trackerCo2.toFixed(1),
    };
  }, [systemSize, sunHours, electricityRate, installCostFixed, installCostTracker]);

  const SliderInput = ({ label, value, onChange, min, max, step, prefix = '', suffix = '', note }) => (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-2">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <span className="text-primary-600 font-bold text-lg">
          {prefix}{typeof value === 'number' && value % 1 !== 0 ? value.toFixed(2) : value}{suffix}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-primary-600"
      />
      <div className="flex justify-between text-xs text-gray-400 mt-1">
        <span>{prefix}{min}{suffix}</span>
        <span>{prefix}{max}{suffix}</span>
      </div>
      {note && <p className="text-xs text-gray-500 mt-1">{note}</p>}
    </div>
  );

  const MetricCard = ({ label, fixed, tracker, unit = '', highlight = false, isCurrency = false }) => {
    const fmt = v => isCurrency ? `$${v.toLocaleString()}` : `${v.toLocaleString()}${unit}`;
    return (
      <div className={`rounded-xl p-5 ${highlight ? 'bg-primary-600 text-white' : 'bg-white border border-gray-100'}`}>
        <p className={`text-sm font-medium mb-3 ${highlight ? 'text-primary-100' : 'text-gray-500'}`}>{label}</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className={`text-xs mb-1 ${highlight ? 'text-primary-200' : 'text-gray-400'}`}>Fixed Panels</p>
            <p className={`font-bold text-lg ${highlight ? 'text-white' : 'text-gray-700'}`}>{fmt(fixed)}</p>
          </div>
          <div>
            <p className={`text-xs mb-1 ${highlight ? 'text-secondary-300' : 'text-primary-500'}`}>DuaAxis Tracker</p>
            <p className={`font-bold text-lg ${highlight ? 'text-secondary-300' : 'text-primary-600'}`}>{fmt(tracker)}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen pt-16 bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block bg-white/20 text-white text-sm font-semibold py-2 px-4 rounded-full mb-4">
            Free Tool
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">ROI Calculator</h1>
          <p className="text-xl text-primary-100 max-w-2xl mx-auto">
            Compare fixed panels vs DuaAxis dual-axis trackers. See your projected savings, payback period, and 25-year return.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

          {/* Inputs */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span className="bg-primary-100 text-primary-600 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold">1</span>
              Configure Your System
            </h2>

            <SliderInput
              label="System Size"
              value={systemSize}
              onChange={setSystemSize}
              min={5} max={2000} step={5}
              suffix=" kW"
              note="Residential: 5–20 kW · Commercial: 20–500 kW · Utility: 500+ kW"
            />
            <SliderInput
              label="Peak Sun Hours / Day"
              value={sunHours}
              onChange={setSunHours}
              min={3} max={8} step={0.5}
              suffix=" hrs"
              note="Egypt average: 5.5 hrs · Desert locations: up to 7 hrs"
            />
            <SliderInput
              label="Electricity Rate"
              value={electricityRate}
              onChange={setElectricityRate}
              min={0.05} max={0.30} step={0.01}
              prefix="$" suffix="/kWh"
            />

            <div className="border-t border-gray-100 pt-6 mt-2">
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-4">Installation Costs (Advanced)</h3>
              <SliderInput
                label="Fixed Panel Cost"
                value={installCostFixed}
                onChange={setInstallCostFixed}
                min={500} max={1500} step={50}
                prefix="$" suffix="/kW"
              />
              <SliderInput
                label="DuaAxis Tracker Cost"
                value={installCostTracker}
                onChange={setInstallCostTracker}
                min={700} max={2000} step={50}
                prefix="$" suffix="/kW"
              />
            </div>
          </div>

          {/* Results */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <span className="bg-primary-100 text-primary-600 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold">2</span>
              Your Projected Results
            </h2>

            {/* DuaAxis Extra benefit callout */}
            <div className="bg-gradient-to-r from-secondary-400 to-secondary-300 rounded-2xl p-6 text-gray-900 shadow-lg">
              <p className="text-sm font-semibold uppercase tracking-wide mb-1">DuaAxis Advantage</p>
              <p className="text-4xl font-bold">${results.extraSavings.toLocaleString()}<span className="text-lg font-normal">/year extra</span></p>
              <p className="text-sm mt-1 opacity-75">{results.extraKwh.toLocaleString()} kWh more energy than fixed panels</p>
            </div>

            <MetricCard
              label="Annual Energy Production"
              fixed={results.fixedAnnualKwh}
              tracker={results.trackerAnnualKwh}
              unit=" kWh"
            />
            <MetricCard
              label="Annual Electricity Savings"
              fixed={results.fixedAnnualSavings}
              tracker={results.trackerAnnualSavings}
              isCurrency
              highlight
            />
            <MetricCard
              label="Total Installation Cost"
              fixed={results.fixedCapex}
              tracker={results.trackerCapex}
              isCurrency
            />
            <MetricCard
              label="Payback Period"
              fixed={results.fixedPayback}
              tracker={results.trackerPayback}
              unit=" years"
            />
            <MetricCard
              label="25-Year ROI"
              fixed={results.fixed25yrROI}
              tracker={results.tracker25yrROI}
              unit="%"
              highlight
            />

            {/* CO2 */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-5">
              <p className="text-sm font-medium text-green-700">🌿 Environmental Impact (DuaAxis)</p>
              <p className="text-2xl font-bold text-green-800 mt-1">{results.trackerCo2} tonnes CO₂/year</p>
              <p className="text-xs text-green-600 mt-1">avoided compared to grid electricity</p>
            </div>

            <a
              href="/#contact"
              className="block w-full text-center bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:-translate-y-1 shadow-lg"
            >
              Get a Formal Quote for Your {systemSize} kW System →
            </a>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-gray-400 text-center mt-10">
          Estimates are based on average performance data and may vary depending on site conditions, panel orientation, shading, and local tariffs. Contact us for a site-specific assessment.
        </p>
      </div>
    </div>
  );
};

export default Calculator;
